/**
 * EkonsultacijeButton - Button komponenta sa eKonsultacije stilovima
 * 
 * Replikuje stilove iz coreclient1/src/style/Buttons.css
 * 
 * Varijante:
 * - primary: Glavni button (#203a5d)
 * - secondary: Sekundarni button (#374e82)
 * - icon: Icon-only button
 * - decline: Decline/unsubscribe button (gray)
 * - file-upload: File upload button
 * - scroll: Scroll to top button
 * 
 * Primer korišćenja:
 * ```jsx
 * <EkonsultacijeButton variant="primary" size="md">
 *   Kreiraj
 * </EkonsultacijeButton>
 * ```
 */

import React from 'react';
import PropTypes from 'prop-types';

export const EkonsultacijeButton = ({
  variant = 'primary',
  size = 'md',
  children,
  className = '',
  disabled = false,
  type = 'button',
  onClick,
  ...props
}) => {
  // Base classes koje se primenjuju na sve varijante
  const baseClasses = 'rounded transition-colors cursor-pointer border-none outline-none';
  
  // Varijante stilova
  const variants = {
    primary: 'bg-ekonsultacije-primary-dark text-white shadow-ekonsultacije hover:bg-ekonsultacije-primary min-w-[90px]', // Tamnija boja za bolji kontrast (WCAG AA)
    secondary: 'bg-ekonsultacije-secondary-DEFAULT text-white shadow-ekonsultacije hover:bg-ekonsultacije-secondary-light',
    icon: 'bg-transparent text-ekonsultacije-secondary-dark hover:text-ekonsultacije-accent-dark',
    'icon-edit': 'bg-transparent text-ekonsultacije-secondary-dark text-2xl hover:text-ekonsultacije-accent-dark',
    'icon-delete': 'bg-transparent text-ekonsultacije-secondary-dark text-lg hover:text-ekonsultacije-accent-dark',
    'icon-create': 'bg-transparent text-ekonsultacije-secondary-dark text-3xl hover:text-ekonsultacije-accent-dark',
    decline: 'bg-gray-500 text-white shadow-md hover:bg-gray-600',
    'file-upload': 'bg-ekonsultacije-secondary-light text-white rounded-lg shadow-ekonsultacije hover:bg-ekonsultacije-secondary-hover text-base max-h-[65px] inline-block text-center',
    scroll: 'bg-ekonsultacije-secondary-lighter rounded-lg shadow-ekonsultacije-lg w-[50px] h-[50px] z-scroll hover:bg-ekonsultacije-secondary-light',
    'only-icon': 'border-0 bg-transparent text-ekonsultacije-primary',
  };
  
  // Veličine
  const sizes = {
    sm: 'px-2 py-1 text-sm',
    md: 'px-4 py-2',
    lg: 'px-6 py-3 text-lg',
    'icon-sm': 'p-1',
    'icon-md': 'p-2',
    'icon-lg': 'p-3',
  };
  
  // Disabled state
  const disabledClasses = disabled 
    ? 'opacity-50 cursor-not-allowed' 
    : '';
  
  // Kombinujemo sve klase
  const buttonClasses = `
    ${baseClasses}
    ${variants[variant] || variants.primary}
    ${sizes[size] || sizes.md}
    ${disabledClasses}
    ${className}
  `.trim().replace(/\s+/g, ' ');
  
  return (
    <button
      type={type}
      className={buttonClasses}
      disabled={disabled}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

EkonsultacijeButton.propTypes = {
  /** Varijanta buttona */
  variant: PropTypes.oneOf([
    'primary',
    'secondary',
    'icon',
    'icon-edit',
    'icon-delete',
    'icon-create',
    'decline',
    'file-upload',
    'scroll',
    'only-icon',
  ]),
  
  /** Veličina buttona */
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'icon-sm', 'icon-md', 'icon-lg']),
  
  /** Sadržaj buttona */
  children: PropTypes.node.isRequired,
  
  /** Dodatne CSS klase */
  className: PropTypes.string,
  
  /** Da li je button disabled */
  disabled: PropTypes.bool,
  
  /** HTML type atribut */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  
  /** Click handler */
  onClick: PropTypes.func,
};

export default EkonsultacijeButton;

